// The in-memory API's agent record (AGENT-RECORD; services/api's `digitalEmployees.record` and
// `.recordEvidence`), with the real routes' access: any reader of the DE's page (`seat_viewer`),
// a DE of another company 404. The fake holds no approvals decided on a DE's output, no
// `intervention.recorded` items and no supervisor time per outcome, so those stay zero, rates
// null, ROI locked 0/4. DE-KPIS-LIVE: the runs ARE the twin's (fake-agents.ts and the other
// twins): each ended top-level run counts once as done, failed or stopped by the production rule
// (classify.ts `runOutcome`), its recorded cost is the week's cost, and the helpers are the
// workforce twin's agents under the DE. Never invented numbers.
import {
  DE_METRIC_CATALOG,
  DE_OUTCOME_CATALOG,
  outcomeRoleOf,
  ROLE_METRIC_CATALOG,
  roleOutcomes,
} from "@companyos/schema/de-metric-catalog";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { isoWeekKey } from "../focus/focus";
import { shiftWeek, weekStart } from "../focus/weeks";
import { type Handler, json, notFound, type Principal, problem } from "./fake";
import type { FakeDe } from "./fake-data";

type DeRecord = OperationOutput<"digitalEmployees.record">;
type RecordFigures = DeRecord["total"];
type Helper = DeRecord["helpers"][number];

const WEEK = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/;
const SIGNALS = new Set([
  "approved_as_is",
  "edited",
  "rejected",
  "completed",
  "reported_outcome",
  "intervention",
  "incident",
  "run",
]);
const ROI_WEEKS = 4;
const DEFAULT_WEEKS = 8;
const MAX_WEEKS = 26;
const DAY = 86_400_000;
/** Micros per minor unit of a two-decimal currency (the twin's companies are USD and EUR). */
const MICROS_PER_MINOR = 10_000;

/** The fields of a twin run the record reads (`runs.list` rows). */
export type RecordRun = {
  id?: string;
  membership_id: string;
  state?: string;
  state_reason?: string | null;
  verdict?: string | null;
  retry_state?: string | null;
  delegation_depth?: number;
  ended_at?: string | null;
  cost_micros?: number;
};

export type AgentRecordDeps = {
  todayWeek: () => string;
  desOf: (companyId: string) => FakeDe[];
  /** DE-KPIS-LIVE: every run the twins hold for the company. */
  runsOf?: (companyId: string) => readonly RecordRun[];
  /** DE-KPIS-LIVE: the helpers (AG) the DE leads or uses. */
  helpersOf?: (p: Principal, deId: string) => Helper[];
};

const STOPPED = new Set([
  "cancelled",
  "review_timeout",
  "taken_over",
  "agent_stopped",
  "agent_gone",
  "membership",
  // TASK-BUDGETS
  "budget",
  "step_cap",
]);
const BUDGET_STOPPED = new Set(["budget", "step_cap"]);
/** Production's `runOutcome` (services/api domain/agent-record/classify.ts). */
export function runOutcomeOf(r: RecordRun): "done" | "failed" | "stopped" | null {
  if ((r.delegation_depth ?? 0) > 0 || !r.ended_at) return null;
  if (r.state === "completed") return r.verdict === "not_verified" ? "failed" : "done";
  if (r.state === "cancelled") return "stopped";
  if (r.state !== "failed") return null;
  const reason = r.state_reason ?? "";
  if (
    (reason === "runtime_lost" || reason === "not_claimed") &&
    (r.retry_state === "scheduled" || r.retry_state === "spawned")
  )
    return null;
  return STOPPED.has(reason) ? "stopped" : "failed";
}

const share = (n: number, of: number) => (of > 0 ? n / of : null);

/** Half-to-even, as the API rounds money. */
function halfEven(x: number): number {
  const f = Math.floor(x);
  const d = x - f;
  if (Math.abs(d - 0.5) < 1e-9) return f % 2 === 0 ? f : f + 1;
  return Math.round(x);
}

/** The run figures of one span, as `figuresOf` derives them. */
function withRuns(f: RecordFigures): RecordFigures {
  const runs = f.runs_done + f.runs_failed + f.runs_stopped;
  return {
    ...f,
    runs,
    run_success_rate: share(f.runs_done, runs),
    cost_per_run_minor: runs > 0 ? halfEven(f.cost_minor / runs) : null,
  };
}

const zero = (): RecordFigures => ({
  runs: 0,
  runs_done: 0,
  runs_failed: 0,
  runs_stopped: 0,
  runs_budget_stopped: 0,
  run_success_rate: null,
  cost_per_run_minor: null,
  memory_reads: 0,
  memory_saves: 0,
  decided: 0,
  escalated: 0,
  reversed: 0,
  approved_as_is: 0,
  edited: 0,
  rejected: 0,
  completed: 0,
  reported_outcomes: 0,
  outcomes: 0,
  interventions: 0,
  incidents: 0,
  cost_minor: 0,
  supervisor_minutes: 0,
  approved_as_is_rate: null,
  edited_rate: null,
  rejected_rate: null,
  cost_per_outcome_minor: null,
  supervisor_minutes_per_outcome: null,
});

const isoDate = (d: Date) => d.toISOString().slice(0, 10);

type Outcome = DeRecord["outcomes"][number];
const ENTRIES = [...DE_METRIC_CATALOG, ...DE_OUTCOME_CATALOG, ...ROLE_METRIC_CATALOG];

/**
 * DE-KPIS-LIVE: the DE's role outcome KPIs as the API derives them (`outcomeRoleOf` its template
 * and seat title), each with the platform's default target. The twin holds no deliveries,
 * messages, briefs, issues, meetings, reviews or reported values: a fed count is 0, a rate or time
 * none, a reported one not tracked yet.
 */
function outcomesOf(de: FakeDe): { outcome_role: string; outcomes: Outcome[] } {
  const role = outcomeRoleOf(de.template_key, de.seat_title);
  const outcomes = roleOutcomes(role).flatMap((o): Outcome[] => {
    const e = ENTRIES.find((m) => m.key === o.key);
    if (!e) return [];
    const reported = e.source === "manual";
    return [
      {
        key: o.key,
        label: o.label ?? e.name,
        unit: e.unit,
        direction: e.direction,
        fed: reported ? "reported" : "companyos",
        tracked: !reported,
        value: reported || e.source === "formula" ? null : 0,
        target: o.target ?? e.default_weekly_target ?? null,
      },
    ];
  });
  return { outcome_role: role, outcomes };
}

export function createAgentRecordHandlers(
  deps: AgentRecordDeps,
): Partial<Record<RouteId, Handler>> {
  const deIn = (p: Principal | null, id: string | undefined) =>
    p ? (deps.desOf(p.company.id).find((d) => d.membership_id === id) ?? null) : null;
  const validation = (detail: string) =>
    problem(422, "validation_failed", "Validation failed", detail);

  return {
    "digitalEmployees.record": ({ principal, params, url }) => {
      const de = deIn(principal, params.id);
      if (!principal || !de) return notFound();
      const raw = url.searchParams.get("weeks");
      const n = raw === null ? DEFAULT_WEEKS : Number(raw);
      if (!Number.isInteger(n) || n < 1 || n > MAX_WEEKS)
        return validation(`weeks must be a whole number from 1 to ${MAX_WEEKS}`);
      const runs = (deps.runsOf?.(principal.company.id) ?? []).filter(
        (r) => r.membership_id === de.membership_id,
      );
      const byWeek = new Map<string, RecordFigures>();
      let ever = false;
      // Cost is the ledger's: every ended run's spend counts, whether or not the run itself
      // counts as an outcome (a worker, a retried loss). Summed in micros, rounded per week.
      const micros = new Map<string, number>();
      for (const r of runs) {
        if (!r.ended_at) continue;
        const key = isoWeekKey(new Date(r.ended_at));
        if (r.cost_micros) micros.set(key, (micros.get(key) ?? 0) + r.cost_micros);
        const outcome = runOutcomeOf(r);
        if (!outcome && !r.cost_micros) continue;
        ever = true;
        const f = byWeek.get(key) ?? zero();
        if (outcome === "done") f.runs_done += 1;
        else if (outcome === "failed") f.runs_failed += 1;
        else if (outcome === "stopped") f.runs_stopped += 1;
        if (outcome === "stopped" && BUDGET_STOPPED.has(r.state_reason ?? ""))
          f.runs_budget_stopped += 1;
        byWeek.set(key, f);
      }
      for (const [key, m] of micros) {
        const f = byWeek.get(key);
        if (f) f.cost_minor = halfEven(m / MICROS_PER_MINOR);
      }
      const weeks = Array.from({ length: n }, (_, i) => {
        const week = shiftWeek(deps.todayWeek(), i - (n - 1));
        const monday = weekStart(week);
        return {
          ...withRuns(byWeek.get(week) ?? zero()),
          week,
          starts_on: isoDate(monday),
          ends_on: isoDate(new Date(monday.getTime() + 6 * DAY)),
        };
      });
      const total = withRuns(
        weeks.reduce((t, w) => {
          t.runs_done += w.runs_done;
          t.runs_failed += w.runs_failed;
          t.runs_stopped += w.runs_stopped;
          t.runs_budget_stopped += w.runs_budget_stopped;
          t.cost_minor += w.cost_minor;
          return t;
        }, zero()),
      );
      const record: DeRecord = {
        de_membership_id: de.membership_id,
        currency: principal.company.reporting_currency,
        weeks,
        total,
        interventions_by_kind: { take_over: 0, cancel: 0 },
        incidents_tracked: ["budget"],
        roi: { state: "locked", weeks_with_outcomes: 0, weeks_needed: ROI_WEEKS },
        has_data: ever,
        window_has_data: total.runs > 0 || total.cost_minor > 0,
        // The twin keeps no budget pace or memory calls: honest empties.
        budget: null,
        budget_readable: true,
        ...outcomesOf(de),
        helpers: deps.helpersOf?.(principal, de.membership_id) ?? [],
        memory: { source: null, brief_use: null },
      };
      return json(record);
    },
    // RECALL-USAGE: the twin has no Recall: the memory use share is off (an honest empty)
    "kpis.memoryUse": ({ principal }) => {
      if (!principal) return notFound();
      const share: OperationOutput<"kpis.memoryUse"> = {
        enabled: false,
        detail: "The memory use share is not switched on.",
        since: null,
        until: null,
        company: { records_delivered: 0, records_used: 0, use_share: null, by_day: [] },
        agents: [],
      };
      return json(share);
    },
    // MEMORY-FIT: the twin has no Recall and no flag: the release gate is off (an honest empty)
    "kpis.memoryGate": ({ principal }) => {
      if (!principal) return notFound();
      const gate: OperationOutput<"kpis.memoryGate"> = {
        enabled: false,
        detail: "The memory gate metrics are not switched on.",
        membership_id: null,
        eval_lift_points: 15,
        release_green: false,
        weeks: [],
      };
      return json(gate);
    },
    "digitalEmployees.recordEvidence": ({ principal, params, url }) => {
      if (!deIn(principal, params.id)) return notFound();
      const week = url.searchParams.get("week") ?? "";
      if (!WEEK.test(week)) return validation("week must be a week key (2026-W43)");
      const signal = url.searchParams.get("signal");
      if (signal !== null && !SIGNALS.has(signal)) return validation(`unknown signal ${signal}`);
      const cursor = url.searchParams.get("cursor");
      if (cursor) return problem(400, "invalid_cursor", "Invalid cursor", "No such page.");
      // DE-KPIS-LIVE: the week's ended runs, newest first, each with its outcome.
      if (signal !== "run" && signal !== null) return json({ data: [], next_cursor: null });
      const de = deIn(principal, params.id);
      const data = (deps.runsOf?.(principal?.company.id ?? "") ?? [])
        .filter((r) => r.membership_id === de?.membership_id && r.id && r.ended_at)
        .filter((r) => isoWeekKey(new Date(r.ended_at as string)) === week)
        .flatMap((r) => {
          const outcome = runOutcomeOf(r);
          return outcome
            ? [
                {
                  id: `run:${r.id}`,
                  signal: "run" as const,
                  at: r.ended_at as string,
                  subject: { kind: "run", id: r.id as string },
                  run_id: r.id as string,
                  detail: outcome,
                  by_membership_id: null,
                  by_name: null,
                  reason: null,
                },
              ]
            : [];
        })
        .sort((a, b) => (a.at < b.at ? 1 : -1));
      return json({ data, next_cursor: null });
    },
  };
}
